<template>
  <el-dialog
    title="新建短篇小说"
    :visible.sync="visibleInner"
    width="720px"
    :close-on-click-modal="false"
    :destroy-on-close="true"
    class="wizard-dialog"
  >
    <div class="wizard">
      <!-- 步骤条 -->
      <div class="wizard-steps">
        <div class="wizard-steps-header">
          <div class="wizard-tip">
            <i class="el-icon-edit-outline" />
            <span v-if="step < 9">还差 <b>{{ 9 - step }}</b> 步，你的故事即将诞生！</span>
            <span v-else>最后一步，马上完成！</span>
          </div>
          <div class="wizard-progress-text">{{ step }} / 9</div>
        </div>
        <div class="wizard-step-line">
          <template v-for="n in 9">
            <div
              :key="'dot-' + n"
              class="wizard-step-item"
              :class="{ active: n === step, done: n < step }"
            >
              <div class="wizard-step-dot">
                <i v-if="n < step" class="el-icon-check" />
                <span v-else>{{ n }}</span>
              </div>
            </div>
            <div
              v-if="n < 9"
              :key="'line-' + n"
              class="wizard-step-connector"
              :class="{ done: n < step }"
            />
          </template>
        </div>
      </div>

      <!-- 步骤内容 -->
      <div class="wizard-body">

        <!-- 1. 选择受众 -->
        <section v-if="step === 1">
          <h3>选择小说受众</h3>
          <p class="sub">请选择您的小说主要面向的读者群体</p>
          <el-row :gutter="16">
            <el-col :span="12">
              <div
                class="audience-card"
                :class="{ active: form.audience === 'male' }"
                @click="form.audience = 'male'"
              >
                <div class="audience-icon">⚔️</div>
                <div class="audience-title">男频</div>
                <div class="audience-tags">玄幻 · 都市 · 历史</div>
              </div>
            </el-col>
            <el-col :span="12">
              <div
                class="audience-card"
                :class="{ active: form.audience === 'female' }"
                @click="form.audience = 'female'"
              >
                <div class="audience-icon">🌸</div>
                <div class="audience-title">女频</div>
                <div class="audience-tags">言情 · 穿越 · 种田</div>
              </div>
            </el-col>
          </el-row>
        </section>

        <!-- 2. 创作思路 -->
        <section v-else-if="step === 2">
          <h3>创作思路</h3>
          <p class="sub">用简洁的语言描述您的创作想法，AI 将基于此为您生成后续内容</p>
          <el-input
            v-model="form.idea"
            type="textarea"
            :rows="7"
            placeholder="例如：一个现代都市青年穿越到古代，成为一名书生，凭借现代知识在古代混得风生水起..."
          />
        </section>

        <!-- 3. 智能推荐模板 -->
        <section v-else-if="step === 3">
          <h3>智能模板推荐</h3>
          <p class="sub">AI 根据您的创作思路自动选择最佳模板，您也可以手动切换</p>
          <div v-if="aiLoading" class="ai-loading">
            <i class="el-icon-loading" />
            <span>AI 正在分析您的创作思路，智能推荐模板中...</span>
          </div>
          <template v-else>
            <div v-if="aiRecommendReason" class="ai-tip">
              <i class="el-icon-magic-stick" /> {{ aiRecommendReason }}
            </div>
            <el-radio-group v-model="form.template" class="template-group">
              <el-radio-button label="ai">AI 智能推荐</el-radio-button>
              <el-radio-button label="romance">情感向</el-radio-button>
              <el-radio-button label="adventure">冒险向</el-radio-button>
            </el-radio-group>
          </template>
        </section>

        <!-- 4. 目标字数 -->
        <section v-else-if="step === 4">
          <h3>设置目标字数</h3>
          <p class="sub">请设置短篇的预计字数，这将帮助 AI 生成更符合篇幅的大纲</p>
          <el-input-number
            v-model="form.targetWords"
            :min="1000"
            :max="200000"
            :step="1000"
          />
          <p class="hint">目标字数仅作参考，实际生成字数取决于您的故事主要情节。</p>
        </section>

        <!-- 5. 故事背景 -->
        <section v-else-if="step === 5">
          <h3>故事背景设定</h3>
          <p class="sub">AI 已为您生成了故事背景，您可以直接修改</p>
          <div v-if="aiLoading" class="ai-loading">
            <i class="el-icon-loading" />
            <span>AI 正在为您构建世界观与故事背景...</span>
          </div>
          <template v-else>
            <el-input
              v-model="form.background"
              type="textarea"
              :rows="7"
              placeholder="故事背景设定将在这里显示..."
            />
            <el-button size="mini" type="primary" plain class="regen-btn" @click="doGenBackground">
              <i class="el-icon-refresh" /> 重新生成
            </el-button>
          </template>
        </section>

        <!-- 6. 角色设定 -->
        <section v-else-if="step === 6">
          <h3>角色设定</h3>
          <p class="sub">AI 已为您生成了主角和配角，点击角色标签可查看和修改详细信息</p>
          <div v-if="aiLoading" class="ai-loading">
            <i class="el-icon-loading" />
            <span>AI 正在为您设计人物角色...</span>
          </div>
          <template v-else>
            <div class="role-section">
              <div class="role-group">
                <div class="role-label">
                  主角
                  <el-button size="mini" type="primary" plain @click="openRoleDialog('main')">新增</el-button>
                </div>
                <div class="role-tags">
                  <el-tag
                    v-for="(role, index) in form.mainRoles"
                    :key="'m-' + index"
                    closable
                    @click.native="editRole('main', index)"
                    @close="removeRole('main', index)"
                  >{{ role.name || '未命名主角' }}</el-tag>
                </div>
              </div>
              <div class="role-group">
                <div class="role-label">
                  配角
                  <el-button size="mini" type="primary" plain @click="openRoleDialog('support')">新增</el-button>
                </div>
                <div class="role-tags">
                  <el-tag
                    v-for="(role, index) in form.supportRoles"
                    :key="'s-' + index"
                    type="info"
                    closable
                    @click.native="editRole('support', index)"
                    @close="removeRole('support', index)"
                  >{{ role.name || '未命名配角' }}</el-tag>
                </div>
              </div>
            </div>
            <el-button size="mini" type="primary" plain class="regen-btn" @click="doGenRoles">
              <i class="el-icon-refresh" /> 重新生成角色
            </el-button>
          </template>
        </section>

        <!-- 7. 故事情节 -->
        <section v-else-if="step === 7">
          <h3>故事主要情节</h3>
          <p class="sub">AI 已为您生成了情节大纲，您可以直接修改</p>
          <div v-if="aiLoading" class="ai-loading">
            <i class="el-icon-loading" />
            <span>AI 正在为您编写故事情节大纲...</span>
          </div>
          <template v-else>
            <el-input
              v-model="form.plot"
              type="textarea"
              :rows="8"
              placeholder="故事情节将在这里显示..."
            />
            <el-button size="mini" type="primary" plain class="regen-btn" @click="doGenPlot">
              <i class="el-icon-refresh" /> 重新生成
            </el-button>
          </template>
        </section>

        <!-- 8. 作品命名 -->
        <section v-else-if="step === 8">
          <h3>作品命名</h3>
          <p class="sub">为您的作品取一个响亮的名字，或从 AI 建议中点击选用</p>
          <el-form label-width="80px">
            <el-form-item label="作者笔名">
              <el-input v-model="form.author" placeholder="请输入您的笔名" />
            </el-form-item>
            <el-form-item label="小说名称">
              <el-input v-model="form.title" placeholder="请输入小说名称" />
            </el-form-item>
          </el-form>
          <div class="title-suggest-wrap">
            <div class="title-suggest-label">
              <i class="el-icon-magic-stick" /> AI 标题建议
              <el-button size="mini" type="text" :loading="titlesLoading" @click="doGenTitles">
                {{ titlesLoading ? '生成中...' : '换一批' }}
              </el-button>
            </div>
            <div v-if="titlesLoading" class="ai-loading small">
              <i class="el-icon-loading" /><span>AI 正在生成标题建议...</span>
            </div>
            <div v-else-if="suggestTitles.length" class="title-suggest-list">
              <el-tag
                v-for="(t, i) in suggestTitles"
                :key="i"
                class="title-tag"
                :class="{ selected: form.title === t }"
                @click.native="form.title = t"
              >{{ t }}</el-tag>
            </div>
          </div>
        </section>

        <!-- 9. 上传封面（可选） -->
        <section v-else-if="step === 9">
          <h3>上传封面</h3>
          <p class="sub">为您的作品上传一个精美的封面（可跳过，后续也可以修改）</p>
          <image-upload
            v-model="form.cover"
            folder-name="cover"
            :limit="1"
            :width="120"
            :height="160"
            :file-type="['jpg','jpeg','png','webp']"
          />
          <p class="hint">点击"创建作品"即可完成短篇小说项目的创建。</p>
        </section>

      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handlePrev" :disabled="step === 1 || aiLoading">上一步</el-button>
      <el-button
        v-if="step < 9"
        type="primary"
        :loading="aiLoading"
        @click="handleNext"
      >
        {{ aiLoading ? 'AI 生成中...' : '下一步' }}
      </el-button>
      <el-button v-else type="primary" :loading="finishing" @click="handleFinish">
        创建作品
      </el-button>
    </span>

    <!-- 角色编辑弹窗 -->
    <el-dialog
      :title="roleDialog.index === -1 ? '新增角色' : '编辑角色'"
      :visible.sync="roleDialog.visible"
      width="520px"
      append-to-body
    >
      <el-form label-width="60px">
        <el-form-item label="姓名">
          <el-input v-model="roleDialog.form.name" maxlength="10" show-word-limit />
        </el-form-item>
        <el-form-item label="性别">
          <el-input v-model="roleDialog.form.gender" maxlength="4" show-word-limit />
        </el-form-item>
        <el-form-item label="年龄">
          <el-input v-model="roleDialog.form.age" maxlength="8" placeholder="如：18岁" show-word-limit />
        </el-form-item>
        <el-form-item label="性格">
          <el-input v-model="roleDialog.form.trait" maxlength="30" show-word-limit />
        </el-form-item>
        <el-form-item label="身份">
          <el-input v-model="roleDialog.form.identity" maxlength="30" show-word-limit />
        </el-form-item>
        <el-form-item label="小传">
          <el-input
            v-model="roleDialog.form.bio"
            type="textarea"
            :rows="4"
            maxlength="150"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="roleDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="saveRole">保存</el-button>
      </span>
    </el-dialog>
  </el-dialog>
</template>

<script>
import { createProject } from '@/api/project';
import { createRole } from '@/api/role';
import {
  generateTemplate,
  generateBackground,
  generateRoles,
  generatePlot,
  generateTitles
} from '@/api/ai';
import ImageUpload from '@/components/ImageUpload';

export default {
  name: 'ShortNovelWizard',
  components: { ImageUpload },
  props: {
    visible: { type: Boolean, default: false }
  },
  data() {
    return {
      visibleInner: this.visible,
      step: 1,
      finishing: false,
      aiLoading: false,
      titlesLoading: false,
      aiRecommendReason: '',
      suggestTitles: [],
      form: {
        audience: '',
        idea: '',
        template: 'ai',
        targetWords: 20000,
        background: '',
        mainRoles: [],
        supportRoles: [],
        plot: '',
        author: '',
        title: '',
        cover: ''
      },
      roleDialog: {
        visible: false,
        type: 'main',
        index: -1,
        form: { name: '', gender: '', age: '', trait: '', identity: '', bio: '' }
      }
    };
  },
  watch: {
    visible(val) {
      this.visibleInner = val;
      if (val) this.reset();
    },
    visibleInner(val) {
      this.$emit('update:visible', val);
    }
  },
  methods: {
    reset() {
      this.step = 1;
      this.finishing = false;
      this.aiLoading = false;
      this.titlesLoading = false;
      this.aiRecommendReason = '';
      this.suggestTitles = [];
      this.form = {
        audience: '',
        idea: '',
        template: 'ai',
        targetWords: 20000,
        background: '',
        mainRoles: [],
        supportRoles: [],
        plot: '',
        author: '',
        title: '',
        cover: ''
      };
    },

    handlePrev() {
      if (this.step > 1) this.step -= 1;
    },

    async handleNext() {
      if (this.step >= 9) return;
      // 进入步骤前做校验 & AI 生成
      const nextStep = this.step + 1;
      if (nextStep === 3) {
        // 进入步骤3：校验 + AI 推荐模板
        if (!this.form.idea || !this.form.idea.trim()) {
          this.$message.warning('请先填写您的创作思路');
          return;
        }
        this.step = nextStep;
        await this.doGenTemplate();
      } else if (nextStep === 5) {
        // 进入步骤5：AI 生成故事背景
        this.step = nextStep;
        if (!this.form.background) {
          await this.doGenBackground();
        }
      } else if (nextStep === 6) {
        // 进入步骤6：AI 生成角色
        this.step = nextStep;
        if (!this.form.mainRoles.length && !this.form.supportRoles.length) {
          await this.doGenRoles();
        }
      } else if (nextStep === 7) {
        // 进入步骤7：AI 生成情节
        this.step = nextStep;
        if (!this.form.plot) {
          await this.doGenPlot();
        }
      } else if (nextStep === 8) {
        // 进入步骤8：AI 生成标题建议
        this.step = nextStep;
        if (!this.suggestTitles.length) {
          await this.doGenTitles();
        }
      } else {
        this.step = nextStep;
      }
    },

    // ---- AI 生成方法 ----

    async doGenTemplate() {
      this.aiLoading = true;
      this.aiRecommendReason = '';
      try {
        const res = await generateTemplate({
          audience: this.form.audience,
          idea: this.form.idea
        });
        if (res.code === 200 && res.data) {
          this.form.template = res.data.template || 'ai';
          this.aiRecommendReason = res.data.reason || '';
        }
      } catch (e) {
        // 失败时保持默认值
      } finally {
        this.aiLoading = false;
      }
    },

    async doGenBackground() {
      this.aiLoading = true;
      try {
        const res = await generateBackground({
          audience: this.form.audience,
          idea: this.form.idea,
          template: this.form.template,
          targetWords: this.form.targetWords
        });
        if (res.code === 200 && res.data) {
          this.form.background = res.data.background || '';
        }
      } catch (e) {
        this.$message.error('背景生成失败，请手动填写');
      } finally {
        this.aiLoading = false;
      }
    },

    async doGenRoles() {
      this.aiLoading = true;
      try {
        const res = await generateRoles({
          audience: this.form.audience,
          idea: this.form.idea,
          background: this.form.background
        });
        if (res.code === 200 && Array.isArray(res.data)) {
          this.form.mainRoles = res.data
            .filter(r => r.type === 'main')
            .map(r => ({ name: r.name, gender: r.gender, age: r.age, trait: r.trait, identity: r.identity, bio: r.bio }));
          this.form.supportRoles = res.data
            .filter(r => r.type === 'support')
            .map(r => ({ name: r.name, gender: r.gender, age: r.age, trait: r.trait, identity: r.identity, bio: r.bio }));
        }
      } catch (e) {
        this.$message.error('角色生成失败，请手动添加');
      } finally {
        this.aiLoading = false;
      }
    },

    async doGenPlot() {
      this.aiLoading = true;
      try {
        const res = await generatePlot({
          audience: this.form.audience,
          idea: this.form.idea,
          background: this.form.background,
          targetWords: this.form.targetWords
        });
        if (res.code === 200 && res.data) {
          this.form.plot = res.data.plot || '';
        }
      } catch (e) {
        this.$message.error('情节生成失败，请手动填写');
      } finally {
        this.aiLoading = false;
      }
    },

    async doGenTitles() {
      this.titlesLoading = true;
      try {
        const res = await generateTitles({
          audience: this.form.audience,
          idea: this.form.idea,
          background: this.form.background,
          plot: this.form.plot
        });
        if (res.code === 200 && Array.isArray(res.data)) {
          this.suggestTitles = res.data;
        }
      } catch (e) {
        // 静默失败
      } finally {
        this.titlesLoading = false;
      }
    },

    // ---- 完成 ----

    async handleFinish() {
      if (!this.form.title || !this.form.title.trim()) {
        this.$message.warning('请先填写小说名称');
        return;
      }
      this.finishing = true;
      try {
        // 1. 创建作品，拿到 projectId
        const res = await createProject({
          title: this.form.title,
          author: this.form.author,
          audience: this.form.audience,
          idea: this.form.idea,
          template: this.form.template,
          targetWords: this.form.targetWords,
          background: this.form.background,
          plot: this.form.plot,
          cover: this.form.cover
        });
        const projectId = res.data && res.data.id;

        // 2. 批量保存 AI 生成的角色
        if (projectId) {
          const allRoles = [
            ...this.form.mainRoles.map(r => ({ ...r, roleType: 'main' })),
            ...this.form.supportRoles.map(r => ({ ...r, roleType: 'support' }))
          ];
          await Promise.all(
            allRoles.map(role =>
              createRole({
                projectId,
                roleType: role.roleType,
                name: role.name,
                gender: role.gender,
                age: role.age,
                trait: role.trait,
                identity: role.identity,
                bio: role.bio
              })
            )
          );
        }

        this.$message.success('作品创建成功！');
        this.$emit('finish');
        this.visibleInner = false;
      } catch (e) {
        // 错误已由 request 拦截器统一提示
      } finally {
        this.finishing = false;
      }
    },

    // ---- 角色弹窗 ----

    openRoleDialog(type) {
      this.roleDialog.type = type;
      this.roleDialog.index = -1;
      this.roleDialog.form = { name: '', gender: '', age: '', trait: '', identity: '', bio: '' };
      this.roleDialog.visible = true;
    },
    editRole(type, index) {
      const list = type === 'main' ? this.form.mainRoles : this.form.supportRoles;
      this.roleDialog.type = type;
      this.roleDialog.index = index;
      this.roleDialog.form = { ...list[index] };
      this.roleDialog.visible = true;
    },
    removeRole(type, index) {
      this.form[type === 'main' ? 'mainRoles' : 'supportRoles'].splice(index, 1);
    },
    saveRole() {
      const key = this.roleDialog.type === 'main' ? 'mainRoles' : 'supportRoles';
      if (this.roleDialog.index === -1) {
        this.form[key].push({ ...this.roleDialog.form });
      } else {
        this.$set(this.form[key], this.roleDialog.index, { ...this.roleDialog.form });
      }
      this.roleDialog.visible = false;
    }
  }
};
</script>

<style scoped>
/* ===== Dialog ===== */
.wizard-dialog ::v-deep .el-dialog {
  border-radius: 16px;
  overflow: hidden;
}
.wizard-dialog ::v-deep .el-dialog__header {
  padding: 20px 24px 16px;
  border-bottom: 1px solid #f5f5f5;
  font-size: 16px;
  font-weight: 600;
}
.wizard-dialog ::v-deep .el-dialog__body {
  padding: 20px 24px 8px;
}
.wizard-dialog ::v-deep .el-dialog__footer {
  padding: 12px 24px 20px;
  border-top: 1px solid #f5f5f5;
}

/* ===== Steps ===== */
.wizard-steps {
  margin-bottom: 24px;
  background: linear-gradient(135deg, #fff5f0 0%, #fff9f7 100%);
  border-radius: 12px;
  padding: 16px 20px;
  border: 1px solid #ffe8dd;
}
.wizard-steps-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.wizard-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #e8633a;
}
.wizard-tip i { font-size: 14px; }
.wizard-tip b { font-weight: 700; color: #ff7f50; }
.wizard-progress-text {
  font-size: 12px;
  color: #c0b4ae;
  font-weight: 500;
  letter-spacing: 0.5px;
}
.wizard-step-line {
  display: flex;
  align-items: center;
}
.wizard-step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}
.wizard-step-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #e8dbd6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: #c0b4ae;
  background: #fff;
  transition: all 0.3s ease;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}
.wizard-step-item.active .wizard-step-dot {
  background: linear-gradient(135deg, #ff9972, #ff7f50);
  border-color: #ff7f50;
  color: #fff;
  box-shadow: 0 4px 12px rgba(255,127,80,0.45);
  transform: scale(1.2);
}
.wizard-step-item.done .wizard-step-dot {
  background: linear-gradient(135deg, #ffb99a, #ff9972);
  border-color: #ffb399;
  color: #fff;
  box-shadow: 0 2px 6px rgba(255,127,80,0.25);
}
.wizard-step-item.done .wizard-step-dot i { font-size: 13px; font-weight: 700; }
.wizard-step-connector {
  flex: 1;
  height: 2px;
  background: #edddd6;
  margin: 0 3px;
  border-radius: 1px;
  transition: background 0.3s ease;
}
.wizard-step-connector.done {
  background: linear-gradient(90deg, #ffb399, #ff9972);
}

/* ===== Body ===== */
.wizard-body { min-height: 200px; }
.wizard-body h3 {
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}
.wizard-body .sub {
  margin: 0 0 20px;
  color: #909399;
  font-size: 13px;
  line-height: 1.6;
}

/* ===== AI Loading ===== */
.ai-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 32px 0;
  color: #ff7f50;
  font-size: 14px;
  justify-content: center;
}
.ai-loading.small { padding: 12px 0; font-size: 13px; }
.ai-loading i { font-size: 20px; animation: spin 1s linear infinite; }
.ai-loading.small i { font-size: 14px; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ===== AI Tip ===== */
.ai-tip {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 10px 14px;
  background: #fff5f0;
  border-left: 3px solid #ff7f50;
  border-radius: 0 8px 8px 0;
  color: #e8633a;
  font-size: 13px;
  margin-bottom: 16px;
  line-height: 1.6;
}

/* ===== Regen Button ===== */
.regen-btn {
  margin-top: 10px;
}

/* ===== Audience Cards ===== */
.audience-card {
  padding: 24px 16px;
  border-radius: 12px;
  border: 2px solid #ebeef5;
  background: #fff;
  cursor: pointer;
  transition: all 0.25s ease;
  text-align: center;
}
.audience-card:hover {
  border-color: #ffcbb5;
  box-shadow: 0 4px 12px rgba(255,127,80,0.12);
}
.audience-card.active {
  border-color: #ff7f50;
  background: linear-gradient(135deg, #fff5f0, #fff);
  box-shadow: 0 6px 20px rgba(255,127,80,0.22);
}
.audience-icon { font-size: 28px; margin-bottom: 8px; }
.audience-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 6px;
  color: #303133;
}
.audience-card.active .audience-title { color: #ff7f50; }
.audience-tags { font-size: 13px; color: #909399; }

/* ===== Template ===== */
.template-group {
  margin-top: 8px;
}

/* ===== Hint ===== */
.hint {
  margin-top: 12px;
  font-size: 12px;
  color: #c0c4cc;
  line-height: 1.6;
}

/* ===== Roles ===== */
.role-section { margin-top: 4px; }
.role-group { margin-bottom: 16px; }
.role-label {
  font-size: 14px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.role-tags .el-tag {
  margin-right: 8px;
  margin-top: 6px;
  cursor: pointer;
  border-radius: 6px;
}

/* ===== Title Suggest ===== */
.title-suggest-wrap {
  margin-top: 16px;
  padding: 14px 16px;
  background: #fafafa;
  border-radius: 10px;
  border: 1px solid #f0f0f0;
}
.title-suggest-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 10px;
}
.title-suggest-label i { color: #ff7f50; }
.title-suggest-list { display: flex; flex-wrap: wrap; gap: 8px; }
.title-tag {
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}
.title-tag:hover {
  border-color: #ff7f50 !important;
  color: #ff7f50 !important;
  background: #fff5f0 !important;
}
.title-tag.selected {
  border-color: #ff7f50 !important;
  color: #fff !important;
  background: #ff7f50 !important;
}
</style>
